import * as nsVue from 'vue'
import { sleep, genUniqueId } from './utils'
import hyper  from './hyper'

type Point = {
  x: number;
  y: number;
}

type Size = {
  width: number;
  height: number;
}

type Rect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface UIState {
  readonly windowSize: Size;
  readonly toasts: ReadonlyArray<Toast>;
  readonly popover: Popover | null;
}

const _state = {
  windowSize: {width: 0, height: 0},
  toasts: [] as Toast[],
  popover: null as Popover | null,
}

export const state: UIState = _state;

{
  let f = () => {
    _state.windowSize.width = window.innerWidth
    _state.windowSize.height = window.innerHeight
  }
  window.addEventListener('resize', f)
  f()
}


type PopoverRenderer = (h: nsVue.CreateElement, ctx: nsVue.RenderContext, dispose: ()=>void) => nsVue.VNode

interface PopoverEvent {
  position: Point;
  readonly target: Element;
}

interface Popover {
  position: Point;
  renderer: PopoverRenderer;
  dispose: () => void;
  onShow? : (e: PopoverEvent) => void;
}

export function popover(position: Point, renderer: PopoverRenderer, onDispose?: () => void, onShow?: (e: PopoverEvent) => void): () => void {
  if (_state.popover) {
    _state.popover.dispose()
  }
  let disposed = false
  let dispose = () => {
    if (!disposed) {
      _state.popover = null
      disposed = true
      if (onDispose) {
        onDispose()
      }
    }
  }
  _state.popover = { position, renderer, dispose, onShow }
  return dispose
}


interface MenuItem {
  label: string;
  action: (() => void) | string | false
}

export function showContextMenu(event: MouseEvent, items: MenuItem[], onDispose?: ()=>void) {
  let renderer: PopoverRenderer = (h, ctx, dispose) => {
    let {div} = hyper(h)
    return div({class: 'context-menu'}, items.map(item => {
      let $click = () => {
        if (typeof(item.action) === 'string') {
          window.location.href = item.action
        }
        else if (item.action) {
          dispose()
          item.action()
        }
      } 
      let disabled$ = item.action === false
      return div({$click, disabled$}, item.label)
    }))
  }
  popover({x: event.clientX, y: event.clientY}, renderer, onDispose, e => {
    let rect = e.target.getBoundingClientRect()
    if (rect.bottom > _state.windowSize.height) {
      if (e.position.y - rect.height > 0) {
        e.position.y -= rect.height
      }
    }
    if (rect.right > _state.windowSize.width) {
      e.position.x -= rect.right - _state.windowSize.width
    }
  })
}


export function exposeAndConfirm(el: HTMLElement, message = ''): Promise<boolean> {
  const gap = 15
  return new Promise<boolean>(resolve => {
    let rect = el.getBoundingClientRect()
    let borderWidth$$ = rect.top + 'px '
                      + Math.max(0, _state.windowSize.width - rect.right) + 'px '
                      + Math.max(0, _state.windowSize.height - rect.bottom) + 'px '
                      + rect.left + 'px'
    let a1 = {
      class: 'ui-expose',
      borderWidth$$,
      width$$: rect.width + 'px',
      height$$: rect.height + 'px',
    }
    let renderer: PopoverRenderer = (h, ctx, dispose) => {
      let close = (value: boolean) => {
        dispose()
        resolve(value)
      }
      let {div, button} = hyper(h)
      return div(a1, [
        div(),
        div({left$$: Math.max(gap, rect.left) + 'px'}, [
          div(message),
          div([
            button({$click: () => close(false)}, '取消'),
            button({class: 'light', $click: () => close(true)}, '确定'),
          ])
        ]),
      ])
    }
    popover({x: 0, y: 0}, renderer, undefined, e => {
      let el = e.target.querySelector('div.ui-expose>div:last-child') as HTMLElement
      let {width, height} = el.getBoundingClientRect()
      if (rect.bottom + gap + height > _state.windowSize.height) {
        el.style.top = -(gap + height) + 'px'
      }
      else {
        el.style.top = (rect.height + gap) + 'px'
      }
    })
  })
}


export function toast(type: ToastType, message: string, detail?: string) {
    let instance = new Toast()
    instance.set(type, message, detail)
    _state.toasts.push(instance)
    return instance
}

type ToastType = 'info' | 'wait' | 'success' | 'error'

class Toast {

  key = genUniqueId()

  type: ToastType = "info"

  message = ""

  detail: string | null = null

  showCloseButton = false

  get closed() {
    return _state.toasts.indexOf(this) !== -1
  }

  set(type: ToastType, message: string, detail?: string) {
    this.type = type
    this.message = message
    if (detail)
      this.detail = detail
    return this
  }

  async close(duration: number | 'manual' = 2000) {
    if (typeof(duration) === 'number') {
      await sleep(duration)
      let list = _state.toasts
      let index = list.indexOf(this)
      if (index !== -1)
        list.splice(index, 1)
    }
    else {
      this.showCloseButton = true
    }
  }
}

